(function () {
  'use strict';

  // Quick Shop can execute this asset again; keep the existing boot state and listeners.
  const existingSupervisor = window.MSPABootSupervisor;
  if (existingSupervisor && typeof existingSupervisor.scan === 'function') {
    existingSupervisor.scan(document);
    return;
  }

  const HOST_SELECTOR = '[data-ms-calculator-host][data-mspa-boot-required="true"]';
  const DEFAULT_BOOT_WINDOW_MS = 7000;
  const DEFAULT_CORE_RETRIES = 2;
  const DEFAULT_FAMILY_RETRIES = 2;
  const RETRY_SETTLE_MS = 520;
  const BOOT_COPY = Object.freeze({
    checkingTitle: 'Gjør klart produktvelgeren …',
    checkingSummary: 'Dette tar vanligvis bare et øyeblikk.',
    retryTitle: 'Produktvelgeren bruker litt lengre tid enn normalt …',
    retrySummary: 'Vi prøver én gang til.',
    failureTitle: 'Produktvelgeren kunne ikke starte.',
    failureSummary: 'Last siden på nytt og prøv igjen.'
  });
  const records = new WeakMap();
  const pendingLoads = new Map();
  const coreState = { retries: 0 };
  const currentScript = document.currentScript;

  function toInt(value, fallback) {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  }

  function hostBootWindow(host) {
    return toInt(host?.getAttribute?.('data-mspa-boot-window-ms'), DEFAULT_BOOT_WINDOW_MS);
  }

  function maxCoreRetries(host) {
    return toInt(host?.getAttribute?.('data-mspa-boot-max-core-retries'), DEFAULT_CORE_RETRIES);
  }

  function maxFamilyRetries(host) {
    return toInt(host?.getAttribute?.('data-mspa-boot-max-family-retries'), DEFAULT_FAMILY_RETRIES);
  }

  function bootRecord(host) {
    if (!records.has(host)) {
      records.set(host, {
        startedAt: Date.now(),
        familyRetries: 0,
        terminal: false,
        mounted: false,
        forcedQuickShop: false,
        checks: 0
      });
    }
    return records.get(host);
  }

  function setAttr(host, name, value) {
    if (!host || !host.setAttribute) return;
    host.setAttribute(name, String(value));
  }

  function setBootState(host, state, reason) {
    if (!host) return;
    setAttr(host, 'data-mspa-boot-state', state);
    if (reason) setAttr(host, 'data-mspa-boot-reason', reason);
    setAttr(host, 'data-mspa-boot-checked-at', String(Date.now()));
  }

  function apiReady() {
    const api = window.MSPurchaseMotor;
    return Boolean(api &&
      typeof api.mount === 'function' &&
      typeof api.update === 'function' &&
      typeof api.add === 'function');
  }

  function motorStateFor(host) {
    try {
      return apiReady() && typeof window.MSPurchaseMotor.getState === 'function'
        ? window.MSPurchaseMotor.getState(host)
        : null;
    } catch (_) {
      return null;
    }
  }

  function hostMounted(host) {
    if (!host || host.getAttribute('data-mspa-boot-state') === 'failed') return false;
    if (!apiReady()) return false;
    if (motorStateFor(host)) return true;
    const errorFalse = host.getAttribute('data-mspa-motor-error') === 'false';
    const hasRenderedShell = Boolean(host.querySelector?.('[data-mspa-motor-shell], [data-mspa-source-role="motor-rendered-family-ui"]'));
    return errorFalse && hasRenderedShell;
  }

  function updateStatusText(host, title, summary, validation, options) {
    const root = host?.querySelector?.('[data-mspa-motor-root]') || host;
    if (!root) return;
    root.querySelectorAll?.('[data-mspa-status]').forEach((node) => {
      node.textContent = title || '';
      node.hidden = !title;
    });
    root.querySelectorAll?.('[data-mspa-summary]').forEach((node) => {
      node.textContent = summary || '';
      node.hidden = !summary;
    });
    root.querySelectorAll?.('[data-mspa-validation]').forEach((node) => {
      node.textContent = validation || '';
      node.hidden = !validation;
    });
    root.querySelectorAll?.('[data-mspa-cta]').forEach((button) => {
      if (options?.preserveProductCta && button.hasAttribute('data-mspa-qp-cta')) return;
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      button.textContent = title || BOOT_COPY.checkingTitle;
    });
  }

  function isBootText(value) {
    return /Gjør klart produktvelgeren|Produktvelgeren bruker litt lengre tid|Dette tar vanligvis bare et øyeblikk|Vi prøver én gang til|MSPA-motoren laster|MSPA kontrollerer|MSPA gjør en kontrollert boot-retry/i.test(String(value || ''));
  }

  function syncMountedStatusText(host, state) {
    const root = host?.querySelector?.('[data-mspa-motor-root]') || host;
    if (!root) return;
    const summary = state && typeof state.summary === 'string' ? state.summary : '';
    const reason = state && typeof state.reason === 'string' ? state.reason : '';
    const validation = state && typeof state.validation === 'string' ? state.validation : '';
    const statusText = validation || summary || reason || '';
    root.querySelectorAll?.('[data-mspa-status]').forEach((node) => {
      node.textContent = statusText;
      node.hidden = !statusText;
      if (state?.status) node.setAttribute('data-mspa-status-type', state.error ? 'error' : state.status);
      else node.removeAttribute?.('data-mspa-status-type');
    });
    root.querySelectorAll?.('[data-mspa-summary]').forEach((node) => {
      node.textContent = summary;
      node.hidden = !summary;
    });
    root.querySelectorAll?.('[data-mspa-validation]').forEach((node) => {
      if (node.hasAttribute?.('data-mspa-field-validation') || node.classList?.contains('mspa-field__validation')) return;
      const text = validation || (state?.error ? reason : '');
      node.textContent = text;
      node.hidden = !text;
    });
  }

  function clearMountedBootText(host) {
    const root = host?.querySelector?.('[data-mspa-motor-root]') || host;
    if (!root) return;
    root.querySelectorAll?.('[data-mspa-status], [data-mspa-summary], [data-mspa-validation]').forEach((node) => {
      if (!isBootText(node.textContent)) return;
      node.textContent = '';
      node.hidden = true;
    });
  }

  function setChecking(host, reason) {
    setBootState(host, 'checking', reason || 'checking');
    setAttr(host, 'data-mspa-motor-status', 'boot-checking');
    setAttr(host, 'data-mspa-motor-ready', 'false');
    setAttr(host, 'data-mspa-motor-busy', 'true');
    setAttr(host, 'data-mspa-motor-disabled', 'true');
    setAttr(host, 'data-mspa-motor-error', 'false');
    updateStatusText(host, BOOT_COPY.checkingTitle, BOOT_COPY.checkingSummary, '', { preserveProductCta: true });
  }

  function setRetrying(host, reason) {
    setBootState(host, 'retrying', reason || 'retrying');
    setAttr(host, 'data-mspa-motor-status', 'boot-retrying');
    setAttr(host, 'data-mspa-motor-ready', 'false');
    setAttr(host, 'data-mspa-motor-busy', 'true');
    setAttr(host, 'data-mspa-motor-disabled', 'true');
    setAttr(host, 'data-mspa-motor-error', 'false');
    updateStatusText(host, BOOT_COPY.retryTitle, BOOT_COPY.retrySummary, '', { preserveProductCta: true });
  }

  function markMounted(host, reason) {
    if (!host || !host.matches?.(HOST_SELECTOR)) return;
    const record = bootRecord(host);
    record.mounted = true;
    record.terminal = true;
    setBootState(host, 'mounted', reason || 'mounted');
    setAttr(host, 'data-mspa-motor-missing', 'false');
    const state = motorStateFor(host);
    if (state) {
      setAttr(host, 'data-mspa-motor-status', state.status || '');
      setAttr(host, 'data-mspa-motor-ready', state.ready ? 'true' : 'false');
      setAttr(host, 'data-mspa-motor-busy', state.busy ? 'true' : 'false');
      setAttr(host, 'data-mspa-motor-disabled', state.disabled ? 'true' : 'false');
      setAttr(host, 'data-mspa-motor-error', state.error ? 'true' : 'false');
      if (state.label) setAttr(host, 'data-mspa-motor-label', state.label);
      if (state.step) setAttr(host, 'data-mspa-motor-step', state.step);
      if (state.summary) setAttr(host, 'data-mspa-motor-summary', state.summary);
      if (state.reason) setAttr(host, 'data-mspa-motor-reason', state.reason);
      if (!state.reason) host.removeAttribute?.('data-mspa-motor-reason');
      syncMountedStatusText(host, state);
    } else {
      clearMountedBootText(host);
    }
    host.classList?.remove('is-mspa-error');
  }

  function failHost(host, reason, message) {
    if (!host || !host.matches?.(HOST_SELECTOR)) return;
    const record = bootRecord(host);
    if (record.terminal && host.getAttribute('data-mspa-boot-state') === 'failed') return;
    record.terminal = true;
    setBootState(host, 'failed', reason);
    setAttr(host, 'data-mspa-motor-missing', 'true');
    setAttr(host, 'data-mspa-motor-error', 'true');
    setAttr(host, 'data-mspa-motor-disabled', 'true');
    setAttr(host, 'data-mspa-motor-ready', 'false');
    setAttr(host, 'data-mspa-motor-busy', 'false');
    setAttr(host, 'data-mspa-motor-status', 'error');
    setAttr(host, 'data-mspa-motor-reason', reason);
    setAttr(host, 'data-mspa-motor-label', BOOT_COPY.failureTitle);
    setAttr(host, 'data-mspa-motor-step', BOOT_COPY.failureTitle);
    setAttr(host, 'data-mspa-motor-summary', BOOT_COPY.failureSummary);
    host.classList?.add('is-mspa-error');
    updateStatusText(host, BOOT_COPY.failureTitle, BOOT_COPY.failureSummary, '');
    console.error('MSPA boot-supervisor:', reason, message || '', host);
  }

  function assetUrl(assetName, host) {
    const explicit = assetName === 'ms-calculator-core.js'
      ? host?.getAttribute?.('data-mspa-boot-core-src')
      : host?.getAttribute?.('data-mspa-boot-family-src');
    if (explicit) return explicit;
    const script = Array.from(document.scripts || []).find((node) => (node.src || '').indexOf(assetName) !== -1);
    if (script?.src) return script.src;
    const base = currentScript?.src || host?.getAttribute?.('data-mspa-boot-core-src') || '';
    if (!base) return '';
    try {
      const url = new URL(base, window.location.href);
      url.pathname = url.pathname.replace(/\/[^/]*$/, '/' + assetName);
      url.search = '';
      return url.href;
    } catch (_) {
      return '';
    }
  }

  function loadScriptOnce(src, key) {
    if (!src) return Promise.reject(new Error('missing script src'));
    const loadKey = key || src;
    if (pendingLoads.has(loadKey)) return pendingLoads.get(loadKey);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'mspa_boot_retry=' + Date.now();
      script.async = false;
      script.defer = true;
      script.setAttribute('data-mspa-boot-retry-script', loadKey);
      script.onload = () => resolve(script);
      script.onerror = () => reject(new Error('script load failed: ' + src));
      (document.head || document.documentElement).appendChild(script);
    }).finally(() => {
      window.setTimeout(() => pendingLoads.delete(loadKey), RETRY_SETTLE_MS);
    });
    pendingLoads.set(loadKey, promise);
    return promise;
  }

  function retryCore(host) {
    if (coreState.retries >= maxCoreRetries(host)) return false;
    coreState.retries += 1;
    setAttr(host, 'data-mspa-boot-core-retries', coreState.retries);
    setRetrying(host, 'core-retry-' + coreState.retries);
    loadScriptOnce(assetUrl('ms-calculator-core.js', host), 'core')
      .catch(() => {})
      .finally(() => scheduleCheck(host, RETRY_SETTLE_MS));
    return true;
  }

  function retryFamily(host, record) {
    const assetName = host.getAttribute('data-mspa-boot-family-asset') || '';
    if (!assetName || record.familyRetries >= maxFamilyRetries(host)) return false;
    record.familyRetries += 1;
    setAttr(host, 'data-mspa-boot-family-retries', record.familyRetries);
    setRetrying(host, 'family-retry-' + record.familyRetries);
    loadScriptOnce(assetUrl(assetName, host), 'family:' + assetName + ':' + (host.getAttribute('data-product-id') || 'host'))
      .catch(() => {})
      .finally(() => scheduleCheck(host, RETRY_SETTLE_MS));
    return true;
  }

  function shouldSkipHost(host, options) {
    if (!host || !host.isConnected) return true;
    if (host.closest?.('template')) return true;
    if (host.closest?.('[data-mspa-hard-disabled-panel]')) return true;
    if (host.hasAttribute('data-mspa-hard-disabled-family')) return true;
    if (host.getAttribute('data-product-available') === 'false') return true;
    const context = host.getAttribute('data-calculator-context') || '';
    if (context === 'quick-shop' && !options?.forceQuickShop && host.closest?.('[data-quick-shop-root][hidden]')) return true;
    return false;
  }

  function checkHost(host, options) {
    if (!host || shouldSkipHost(host, options)) return;
    const record = bootRecord(host);
    if (options?.forceQuickShop) record.forcedQuickShop = true;
    if (record.terminal && host.getAttribute('data-mspa-boot-state') !== 'failed') return;
    if (hostMounted(host)) {
      markMounted(host, 'mounted');
      return;
    }
    record.checks += 1;
    setAttr(host, 'data-mspa-boot-check-count', record.checks);
    const elapsed = Date.now() - record.startedAt;
    if (elapsed > hostBootWindow(host)) {
      failHost(
        host,
        apiReady() ? 'family-boot-timeout' : 'core-load-failed',
        apiReady()
          ? 'MSPA family-port startet ikke innen boot-vinduet. Ingen gammel runtime-fallback brukes.'
          : 'MSPA core kunne ikke starte innen boot-vinduet. Ingen gammel runtime-fallback brukes.'
      );
      return;
    }
    setChecking(host, apiReady() ? 'family-checking' : 'core-checking');
    if (!apiReady()) {
      if (retryCore(host)) return;
      scheduleCheck(host, Math.min(900, Math.max(220, hostBootWindow(host) - elapsed)));
      return;
    }
    if (retryFamily(host, record)) return;
    scheduleCheck(host, Math.min(900, Math.max(220, hostBootWindow(host) - elapsed)));
  }

  function scheduleCheck(host, delay, options) {
    window.setTimeout(() => checkHost(host, options || {}), Math.max(0, delay || 0));
  }

  function scan(scope, options) {
    const root = scope && scope.querySelectorAll ? scope : document;
    const hosts = root.matches?.(HOST_SELECTOR) ? [root] : Array.from(root.querySelectorAll?.(HOST_SELECTOR) || []);
    hosts.forEach((host) => {
      if (shouldSkipHost(host, options)) return;
      const state = host.getAttribute('data-mspa-boot-state');
      if (!state || state === 'pending') setChecking(host, 'initial-check');
      scheduleCheck(host, 0, options || {});
    });
    return hosts;
  }

  document.addEventListener('ms:mspa-motor:mount', (event) => {
    const root = event.detail?.root;
    const host = root?.matches?.(HOST_SELECTOR) ? root : root?.closest?.(HOST_SELECTOR);
    if (host) markMounted(host, 'motor-mount');
  });

  document.addEventListener('ms:mspa-motor:update', (event) => {
    const host = event.target?.matches?.(HOST_SELECTOR) ? event.target : event.target?.closest?.(HOST_SELECTOR);
    if (host && event.detail?.error !== true) markMounted(host, 'motor-update');
  });

  document.addEventListener('shopify:section:load', (event) => {
    scan(event.target || document);
  });

  document.addEventListener('ms:quick-shop:loaded', (event) => {
    scan(event.detail?.content || event.target || document, { forceQuickShop: true });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  } else {
    scan(document);
  }

  window.MSPABootSupervisor = {
    version: '0.1.1',
    scan,
    check: checkHost,
    fail: failHost,
    isApiReady: apiReady,
    isMounted: hostMounted
  };
})();
